import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Users, Mail, Flag, Mic, TrendingUp, TrendingDown, Loader2,
  BarChart3, PieChart, Activity, Globe, Building, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#00457f', '#d4af37', '#22c55e', '#ef4444', '#8b5cf6', '#f59e0b'];

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [leadAnalytics, setLeadAnalytics] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [golfAnalytics, setGolfAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAnalytics();
  }, [navigate]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [overviewRes, leadsRes, campaignsRes, golfRes] = await Promise.all([
        fetch(`${API_URL}/api/settings/analytics/overview`),
        fetch(`${API_URL}/api/settings/analytics/leads?days=30`),
        fetch(`${API_URL}/api/settings/analytics/campaigns`),
        fetch(`${API_URL}/api/settings/analytics/golf`)
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (leadsRes.ok) setLeadAnalytics(await leadsRes.json());
      if (campaignsRes.ok) setCampaignAnalytics(await campaignsRes.json());
      if (golfRes.ok) setGolfAnalytics(await golfRes.json());
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'campaigns', label: 'Campaigns', icon: Mail },
    { id: 'golf', label: 'Golf Outing', icon: Flag }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Track your website and campaign performance</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#00457f] border-b-2 border-[#00457f]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Total Leads"
                value={overview.leads.total}
                subtitle={`${overview.leads.last_30_days} in last 30 days`}
                icon={Users}
                color="blue"
              />
              <StatCard
                title="Golf Inquiries"
                value={overview.golf.total}
                subtitle={`${overview.golf.pending} pending contact`}
                icon={Flag}
                color="green"
              />
              <StatCard
                title="Speakers"
                value={overview.speakers.total}
                subtitle={`${overview.speakers.confirmed} confirmed`}
                icon={Mic}
                color="purple"
              />
              <StatCard
                title="Email Campaigns"
                value={overview.campaigns.total}
                subtitle={`${overview.campaigns.sent} sent`}
                icon={Mail}
                color="amber"
              />
            </div>

            {/* Quick Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Leads Over Time */}
              {leadAnalytics?.over_time && leadAnalytics.over_time.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Leads Over Time</CardTitle>
                    <CardDescription>Last 30 days</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={leadAnalytics.over_time}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="count" stroke="#00457f" strokeWidth={2} dot={{ fill: '#00457f' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Leads by Country */}
              {leadAnalytics?.by_country && leadAnalytics.by_country.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Leads by Country</CardTitle>
                    <CardDescription>Top 10 countries</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={leadAnalytics.by_country} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tick={{ fontSize: 12 }} />
                          <YAxis dataKey="country" type="category" tick={{ fontSize: 12 }} width={100} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#00457f" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && leadAnalytics && (
          <div className="space-y-6">
            {/* Leads Over Time */}
            <Card>
              <CardHeader>
                <CardTitle>Registrations Over Time</CardTitle>
                <CardDescription>Daily registration count for the last 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                {leadAnalytics.over_time.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={leadAnalytics.over_time}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="#00457f" strokeWidth={2} name="Registrations" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState message="No registration data available" />
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* By Country */}
              <Card>
                <CardHeader>
                  <CardTitle>By Country</CardTitle>
                  <CardDescription>Top 10 countries by registration</CardDescription>
                </CardHeader>
                <CardContent>
                  {leadAnalytics.by_country.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={leadAnalytics.by_country}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="count"
                            nameKey="country"
                            label={({ country, percent }) => `${country} ${(percent * 100).toFixed(0)}%`}
                          >
                            {leadAnalytics.by_country.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <EmptyState message="No country data available" />
                  )}
                </CardContent>
              </Card>

              {/* By Organization */}
              <Card>
                <CardHeader>
                  <CardTitle>By Organization</CardTitle>
                  <CardDescription>Top 10 organizations</CardDescription>
                </CardHeader>
                <CardContent>
                  {leadAnalytics.by_organization.length > 0 ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={leadAnalytics.by_organization} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tick={{ fontSize: 12 }} />
                          <YAxis dataKey="organization" type="category" tick={{ fontSize: 10 }} width={120} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#d4af37" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <EmptyState message="No organization data available" />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && campaignAnalytics && (
          <div className="space-y-6">
            {/* Email Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Total Sent"
                value={campaignAnalytics.totals.sent}
                icon={Mail}
                color="blue"
              />
              <StatCard
                title="Opened"
                value={campaignAnalytics.totals.opened}
                subtitle={`${campaignAnalytics.totals.open_rate}% open rate`}
                icon={Activity}
                color="green"
              />
              <StatCard
                title="Clicked"
                value={campaignAnalytics.totals.clicked}
                subtitle={`${campaignAnalytics.totals.click_rate}% click rate`}
                icon={TrendingUp}
                color="purple"
              />
              <StatCard
                title="Bounced"
                value={campaignAnalytics.totals.bounced}
                icon={TrendingDown}
                color="red"
              />
            </div>

            {/* Campaign Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Campaigns by Status</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(campaignAnalytics.by_status).length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={Object.entries(campaignAnalytics.by_status).map(([status, count]) => ({ status, count }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="count"
                            nameKey="status"
                            label={({ status, count }) => `${status}: ${count}`}
                          >
                            {Object.entries(campaignAnalytics.by_status).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <EmptyState message="No campaign data available" />
                  )}
                </CardContent>
              </Card>

              {/* Recent Campaigns */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Campaigns</CardTitle>
                </CardHeader>
                <CardContent>
                  {campaignAnalytics.recent_campaigns.length > 0 ? (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {campaignAnalytics.recent_campaigns.map((campaign, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg">
                          <p className="font-medium text-gray-900 truncate">{campaign.subject || 'No subject'}</p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                            <span className={`px-2 py-0.5 rounded capitalize ${
                              campaign.status === 'sent' ? 'bg-green-100 text-green-700' :
                              campaign.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {campaign.status}
                            </span>
                            {campaign.stats && (
                              <span>Sent: {campaign.stats.sent || 0}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState message="No campaigns yet" />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Golf Tab */}
        {activeTab === 'golf' && golfAnalytics && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* By Participation Type */}
              <Card>
                <CardHeader>
                  <CardTitle>By Participation Interest</CardTitle>
                </CardHeader>
                <CardContent>
                  {golfAnalytics.by_participation.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={golfAnalytics.by_participation}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="count"
                            nameKey="type"
                            label={({ type, percent }) => `${(percent * 100).toFixed(0)}%`}
                          >
                            {golfAnalytics.by_participation.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <EmptyState message="No participation data available" />
                  )}
                </CardContent>
              </Card>

              {/* By Sponsorship Interest */}
              <Card>
                <CardHeader>
                  <CardTitle>By Sponsorship Interest</CardTitle>
                </CardHeader>
                <CardContent>
                  {golfAnalytics.by_sponsorship.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={golfAnalytics.by_sponsorship}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="type" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#22c55e" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <EmptyState message="No sponsorship data available" />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Inquiries Over Time */}
            <Card>
              <CardHeader>
                <CardTitle>Inquiries Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                {golfAnalytics.over_time.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={golfAnalytics.over_time}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="count" stroke="#22c55e" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState message="No timeline data available" />
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EmptyState = ({ message }) => (
  <div className="flex items-center justify-center h-full text-gray-400">
    <p>{message}</p>
  </div>
);

export default AdminAnalytics;
